/**
 * The Planner's headline (spec 2026-10-14 §5): the picked day's relative name ("Today", "Tomorrow",
 * "Yesterday", else the weekday) in the large title, a Today button beside it on any other day, and
 * the date line under it. On phones the date line is the button that swaps the week strip for the
 * month (`monthToggle`); where the month always shows it is plain text, hidden from screen readers
 * because the heading's label already says it.
 */
import React from 'react';
import { StyleSheet, View } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { formatDateLine, isSameDay, relativeDayName } from '@/utils/residents/todoCalendar';

/** 12pt around the ~20pt "Today" label: a 44pt target of the button's own (react-native-web ignores hitSlop). */
const TODAY_REACH = 12;
/**
 * Below the ~20pt date line. The wrap gap above it (space.xs) is its top reach, so the 44pt target starts at the
 * title row's bottom and never covers the title or the Today label. On phones the spec puts the week strip
 * space.xl (20pt) below the title block, so the target never covers the strip either.
 */
const DATE_LINE_REACH = 20;
const CHEVRON_SIZE = 14;

export interface DayTitleProps {
  date: Date;
  /** "Now": what Today / Tomorrow / Yesterday mean, and whether the Today button shows. */
  now: Date;
  /** Appended to the spoken heading after "{title}, {date line}" — e.g. ", 4 due, next at 3:00 PM". */
  labelSuffix?: string;
  onToday: () => void;
  /** Phones: the date line opens and closes the month. Omit where the month always shows. */
  monthToggle?: { open: boolean; onToggle: () => void };
}

export const DayTitle: React.FC<DayTitleProps> = ({ date, now, labelSuffix = '', onToday, monthToggle }) => {
  const type = useTypography();
  const space = useSpacing();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      wrap: {
        gap: space.xs,
      },
      // Wraps: when the title and the Today button don't fit side by side, the button drops under.
      titleRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        columnGap: space.md,
      },
      title: {
        ...type.largeTitle,
        color: colors.text.primary,
      },
      todayText: {
        ...type.subhead,
        fontWeight: '600',
        color: semantic.interactive.primaryHover,
      },
      // The Today button's own 44pt box: the padding is the reach, and the negative margin cancels
      // it so the title row lays out as before.
      todayButton: {
        paddingVertical: TODAY_REACH,
        paddingHorizontal: TODAY_REACH,
        marginVertical: -TODAY_REACH,
        marginHorizontal: -TODAY_REACH,
      },
      // The phone date line's own 44pt box: its top reach is the wrap gap, so the box starts at the title row's
      // bottom; the negative margins keep the layout.
      dateRow: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: space.xs,
        paddingTop: space.xs,
        marginTop: -space.xs,
        paddingBottom: DATE_LINE_REACH,
        marginBottom: -DATE_LINE_REACH,
      },
      // Shrinks first, so a long line wraps rather than pushing the chevron off a 320pt phone.
      dateLine: {
        ...type.subhead,
        flexShrink: 1,
        color: colors.text.quiet,
      },
      chevron: {
        color: colors.text.quiet,
      },
    }),
  );

  const title = relativeDayName(date, now);
  const dateLine = formatDateLine(date, now);
  const isToday = isSameDay(date, now);

  return (
    <View style={styles.wrap}>
      <View style={styles.titleRow}>
        <Text
          style={styles.title}
          accessibilityRole="header"
          accessibilityLabel={`${title}, ${dateLine}${labelSuffix}`}
        >
          {title}
        </Text>
        {isToday ? null : (
          <Hoverable
            hover="link"
            style={styles.todayButton}
            onPress={onToday}
            accessibilityRole="button"
            accessibilityLabel="Go to today"
          >
            {({ textStyle }) => <Text style={[styles.todayText, textStyle]}>Today</Text>}
          </Hoverable>
        )}
      </View>
      {monthToggle ? (
        <Hoverable
          hover="none"
          style={styles.dateRow}
          onPress={monthToggle.onToggle}
          accessibilityRole="button"
          accessibilityLabel={monthToggle.open ? 'Show week' : 'Show month'}
          accessibilityState={{ expanded: monthToggle.open }}
        >
          <Text style={styles.dateLine}>{dateLine}</Text>
          <Icon
            name={monthToggle.open ? 'chevron-up' : 'chevron-down'}
            size={CHEVRON_SIZE}
            style={styles.chevron}
          />
        </Hoverable>
      ) : (
        <Text
          style={styles.dateLine}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          aria-hidden
        >
          {dateLine}
        </Text>
      )}
    </View>
  );
};

DayTitle.displayName = 'DayTitle';
